import { useRef, useState } from 'react';
import FormAlert from '../reference/FormAlert';
import { apiErrorMessage } from '../../services/apiErrors';
import { formatTons, monthLabel, requirementsApi } from '../../services/requirementsApi';
import type { ImportPreview } from '../../services/requirementsApi';

interface Props {
  slug: string;
  /** The first month of the grid on screen, so the template matches it. */
  start: string;
  months: number;
  /** Called with how many cells were saved, so the page can reload. */
  onSaved: (changed: number) => void;
  onClose: () => void;
}

/** Fill the grid from a spreadsheet: download the template, upload it, see every change, then save. */
export default function ImportPanel({ slug, start, months, onSaved, onClose }: Props) {
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [fileName, setFileName] = useState('');
  const [busy, setBusy] = useState<'template' | 'check' | 'save' | null>(null);
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const download = async () => {
    setError('');
    setBusy('template');
    try {
      const { data } = await requirementsApi.template(slug, start, months);
      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${slug}-requirements-${start}.xlsx`;
      link.click();
      // Revoking at once can cancel the download in some browsers.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setError(apiErrorMessage(err, 'We could not make the template just now. Check your connection and try again.'));
    } finally {
      setBusy(null);
    }
  };

  const check = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setPreview(null);
    setFileName(file.name);
    setBusy('check');
    try {
      const { data } = await requirementsApi.previewImport(slug, file);
      setPreview(data);
    } catch (err) {
      setError(apiErrorMessage(err, 'We could not read that file just now. Check your connection and try again.'));
    } finally {
      setBusy(null);
      // Picking the same file again after fixing it should check it again.
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const save = async () => {
    if (!preview || preview.problems.length || !preview.changes.length) return;
    setError('');
    setBusy('save');
    try {
      const cells = preview.changes.map((c) => ({
        mill_id: c.mill_id,
        ingredient_id: c.ingredient_id,
        month: c.month,
        short_tons: c.new_short_tons,
        old_short_tons: c.old_short_tons,
      }));
      const { data } = await requirementsApi.save(slug, cells, 'import');
      onSaved(data.changed);
    } catch (err) {
      setError(apiErrorMessage(err, 'We could not save the file just now. Check your connection and try again.'));
      setBusy(null);
    }
  };

  const count = preview?.changes.length ?? 0;

  return (
    <section aria-labelledby="rq-import" className="mt-6 space-y-5 rounded-md border border-line bg-surface p-6">
      <div>
        <h2 id="rq-import" className="text-heading text-ink">
          Import from a spreadsheet
        </h2>
        <p className="mt-1 max-w-2xl text-body text-ink-muted">
          Download the template, fill in the short tons, and upload it. A blank box leaves that month as it is. You
          see every change before anything is saved.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button type="button" className="btn-secondary" onClick={download} disabled={busy !== null}>
          {busy === 'template' ? 'Making template' : 'Download template'}
        </button>
        <label htmlFor="rq-file" className={`btn-secondary cursor-pointer ${busy !== null ? 'pointer-events-none opacity-60' : ''}`}>
          {busy === 'check' ? 'Checking file' : 'Upload filled-in file'}
        </label>
        <input
          id="rq-file"
          ref={fileRef}
          type="file"
          accept=".xlsx,.csv"
          className="sr-only"
          onChange={check}
          disabled={busy !== null}
        />
        <button type="button" className="text-caption font-semibold text-navy underline-offset-4 hover:underline" onClick={onClose}>
          Close
        </button>
      </div>

      <FormAlert lead="Import not done." message={error} />

      {preview && (
        <div className="space-y-4">
          <p className="text-body text-ink" role="status">
            <span className="font-semibold">{fileName}</span>:{' '}
            {count === 1 ? '1 change' : `${count} changes`}
            {preview.unchanged > 0 && `, ${preview.unchanged} already match`}
            {preview.problems.length > 0 &&
              `, ${preview.problems.length === 1 ? '1 problem' : `${preview.problems.length} problems`}`}
            .
          </p>

          {preview.problems.length > 0 && (
            <div role="alert" className="rounded-md border border-danger bg-danger-tint p-3 text-caption text-danger">
              <p className="font-semibold">Fix these rows in the file and upload it again. Nothing has been saved.</p>
              <ul className="mt-2 list-disc space-y-1 pl-5">
                {preview.problems.map((p, n) => (
                  <li key={n}>
                    Row {p.row}: {p.message}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {count > 0 && (
            <div className="relative max-h-80 overflow-auto border-y border-line">
              <table className="w-full border-collapse text-body">
                <caption className="sr-only">Changes the file would make</caption>
                <thead className="sticky top-0 bg-surface">
                  <tr className="border-b border-line-strong text-left text-caption font-semibold text-ink-muted">
                    <th scope="col" className="py-2 pr-4">
                      Mill
                    </th>
                    <th scope="col" className="py-2 pr-4">
                      Ingredient
                    </th>
                    <th scope="col" className="py-2 pr-4">
                      Month
                    </th>
                    <th scope="col" className="py-2 pr-4 text-right">
                      Now
                    </th>
                    <th scope="col" className="py-2 text-right">
                      From file
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {preview.changes.map((c) => (
                    <tr key={`${c.mill_id}|${c.ingredient_id}|${c.month}`} className="border-b border-line">
                      <td className="py-2 pr-4 font-semibold text-ink">{c.mill_name}</td>
                      <td className="py-2 pr-4 text-ink">{c.ingredient_name}</td>
                      <td className="py-2 pr-4 whitespace-nowrap text-ink">{monthLabel(c.month)}</td>
                      <td className="py-2 pr-4 text-right tabular-nums text-ink-muted">
                        {c.old_short_tons === null ? 'Blank' : formatTons(c.old_short_tons)}
                      </td>
                      <td className="py-2 text-right font-semibold tabular-nums text-ink">{formatTons(c.new_short_tons)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {count === 0 && preview.problems.length === 0 && (
            <p className="text-body text-ink-muted">The file matches what is saved. There is nothing to change.</p>
          )}

          <button
            type="button"
            className="btn-primary"
            onClick={save}
            disabled={busy !== null || count === 0 || preview.problems.length > 0}
          >
            {busy === 'save' ? 'Saving' : count === 1 ? 'Save 1 change from the file' : `Save ${count} changes from the file`}
          </button>
        </div>
      )}
    </section>
  );
}
